/** @jsx isolateComponent */
import React, { ReactNode } from 'react';
import { isFunction } from 'lodash';

import { InterpolationWithTheme } from '@emotion/core';
import { isolateComponent } from '@zapier/style-encapsulation';

import {
  FormLabel,
  Spacer,
  Text,
  TextInput as DSTextInput,
} from '@zapier/design-system';

export type TextInputField = {
  /** Additional content to describe this field. */
  details?: ReactNode;
  /** The unique ID of this field. */
  id: string;
  /** Is the TextInput multiline? */
  isMultiline?: boolean;
  /** Is this field required? */
  isRequired?: boolean;
  /** The label that describes this field. */
  label: string;
  /** Placeholder text that appears in the TextInput. */
  placeholder?: string;
  /** Identifies this field type. */
  type: 'text';
};

export type TextInputProps = {
  /** The callback function to fire when the field changes. */
  onChange: (value: string) => void;
  /** Static properties about this field. */
  staticProperties: TextInputField;
  /** A prefilled value from some higher state. */
  value?: string;
};

const Styles = {
  details: {
    lineHeight: 1.5,
    margin: '5px 0 10px 0',
    // Override problematic default browser styles.
    'ol, ul': {
      listStylePosition: 'inside',
      margin: 0,
      padding: 0,
    },
  },
};

/**
 * A text input form field.
 */
const TextInput = ({
  onChange,
  staticProperties: {
    details,
    id,
    isMultiline,
    isRequired,
    label,
    placeholder,
  },
  value,
}: TextInputProps) => {
  return (
    <div>
      <FormLabel htmlFor={id} isRequired={isRequired} size="small">
        {label}
      </FormLabel>
      {!details && <Spacer height={5} />}
      {details && (
        <Text css={Styles.details as InterpolationWithTheme<any>}>
          {isFunction(details) ? details() : details}
        </Text>
      )}
      <DSTextInput
        aria-required={isRequired}
        id={id}
        isMultiline={isMultiline}
        onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
          onChange(evt.currentTarget.value)
        }
        placeholder={placeholder}
        resize="vertical"
        rows={4}
        value={value}
      />
    </div>
  );
};

export default TextInput;
